import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Download, FileText, Image, File, User, AlertCircle, Eye, RefreshCw, BarChart3, Maximize2, Layers, BarChart2, Folder, MessageSquare, Loader2 } from 'lucide-react';
import FileViewerModal from './FileViewerModal';
import MarkdownExpandModal from './MarkdownExpandModal';
import ImageThumbnail from './ImageThumbnail';
import CsvThumbnail from './CsvThumbnail';
import { getApiBaseUrl } from '../config/backend.config';

function MessageList({ messages, onDownloadFile, onRetry, onConvertToReport, onSaveReport, messagesEndRef, workspaceId, getAuthToken, onAddToChat }) {
  const [viewerModal, setViewerModal] = useState({
    isOpen: false,
    fileUrl: null,
    filename: null,
    fileType: null,
    fileSize: null
  });

  const [expandModal, setExpandModal] = useState({
    isOpen: false,
    content: null,
    timestamp: null
  });

  // Track loading state for inline file buttons
  const [loadingFiles, setLoadingFiles] = useState({});

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const isImageFile = (filename) => {
    const ext = filename?.split('.').pop()?.toLowerCase();
    return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext);
  };

  const isCsvFile = (filename) => {
    return filename?.split('.').pop()?.toLowerCase() === 'csv';
  };

  const getFileIcon = (filename, fileType) => {
    const ext = filename.split('.').pop().toLowerCase();

    if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext)) {
      return <Image size={16} />;
    } else if (['txt', 'md', 'csv', 'json', 'xml', 'yaml', 'yml'].includes(ext)) {
      return <FileText size={16} />;
    } else {
      return <File size={16} />;
    }
  };

  const openFileViewer = (fileUrl, filename, fileType, fileSize) => {
    setViewerModal({
      isOpen: true,
      fileUrl,
      filename,
      fileType,
      fileSize
    });
  };

  const closeFileViewer = () => {
    setViewerModal({
      isOpen: false,
      fileUrl: null,
      filename: null,
      fileType: null,
      fileSize: null
    });
  };

  const openExpandModal = (content, timestamp) => {
    setExpandModal({
      isOpen: true,
      content,
      timestamp
    });
  };

  const closeExpandModal = () => {
    setExpandModal({
      isOpen: false,
      content: null,
      timestamp: null
    });
  };

  /**
   * Fetch a presigned URL for an S3 file on-demand (lazy loading).
   * This is called when the user clicks View or Download on an inline file attachment.
   */
  const fetchPresignedUrl = async (s3Key, fileId) => {
    if (!workspaceId || !getAuthToken) {
      console.error('Missing workspaceId or getAuthToken for presigned URL fetch');
      return null;
    }

    // Set loading state for this specific file
    setLoadingFiles(prev => ({ ...prev, [fileId]: true }));

    try {
      const token = await getAuthToken();
      if (!token) {
        throw new Error('Unable to get authentication token');
      }

      const apiBaseUrl = getApiBaseUrl();
      const queryParams = new URLSearchParams({
        s3_key: s3Key,
        workspace_id: workspaceId
      }).toString();

      const response = await fetch(`${apiBaseUrl}/files/presigned-url?${queryParams}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to get presigned URL');
      }

      const data = await response.json();
      return data.file_url;
    } catch (error) {
      console.error('Error fetching presigned URL:', error);
      return null;
    } finally {
      setLoadingFiles(prev => ({ ...prev, [fileId]: false }));
    }
  };

  /**
   * Handle View button click for inline file attachments.
   * Fetches presigned URL then opens the file viewer modal.
   */
  const handleInlineFileView = async (file, fileId) => {
    const presignedUrl = await fetchPresignedUrl(file.s3_key, fileId);
    if (presignedUrl) {
      openFileViewer(presignedUrl, file.filename, file.file_type, file.file_size);
    }
  };

  /**
   * Handle Download button click for inline file attachments.
   * Fetches presigned URL then triggers download.
   */
  const handleInlineFileDownload = async (file, fileId) => {
    const presignedUrl = await fetchPresignedUrl(file.s3_key, fileId);
    if (presignedUrl) {
      // Open in new tab to trigger download
      window.open(presignedUrl, '_blank');
    }
  };

  const isMobile = () => window.innerWidth <= 768;

  const renderMessage = (message) => {
    switch (message.type) {
      case 'user':
        return (
          <div key={message.id} className="message user">
            <div className="message-header">
              <User size={16} className="message-icon" />
              <span className="message-sender">You</span>
            </div>
            <div className="message-content">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
            </div>
            <div className="message-timestamp">
              {formatTimestamp(message.timestamp)}
            </div>
          </div>
        );

      case 'agent':
        // Custom table component that wraps tables with expand functionality
        const TableWrapper = ({ children, ...props }) => (
          <div
            className="table-expand-wrapper"
            onClick={() => openExpandModal(message.content, message.timestamp)}
            title="Click to expand"
          >
            <table {...props}>{children}</table>
            <div className="table-expand-overlay">
              <Maximize2 size={16} />
              <span>Click to expand</span>
            </div>
          </div>
        );

        return (
          <div key={message.id} className="message agent">
            <div className="agent-avatar-container">
              <img src="/maya.jpg" alt="Maya" className="agent-avatar" />
              <span className="agent-name">Maya</span>
              <span className="agent-timestamp">{formatTimestamp(message.timestamp)}</span>
            </div>
            <div className="message-content-wrapper">
            <div
              className="message-content"
              onClick={(e) => {
                // On mobile, tap message content to expand — but not if tapping a button/link/file/table
                if (!isMobile()) return;
                if (e.target.closest('button, a, .inline-file-attachments, .table-expand-wrapper')) return;
                openExpandModal(message.content, message.timestamp);
              }}
            >
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  table: TableWrapper
                }}
              >{message.content}</ReactMarkdown>
              {/* Inline file attachments - inside message content */}
              {message.files && message.files.length > 0 && (() => {
                const imageFiles = message.files.filter(f => isImageFile(f.filename));
                const csvFiles = message.files.filter(f => isCsvFile(f.filename));
                const otherFiles = message.files.filter(f => !isImageFile(f.filename) && !isCsvFile(f.filename));
                return (
                  <div className="inline-file-attachments" style={{ marginTop: '0.75rem' }}>
                    {/* Image thumbnails */}
                    {imageFiles.length > 0 && (
                      <div className="image-thumbnails-grid">
                        {imageFiles.map((file, index) => {
                          const fileId = `${message.id}-img-${index}`;
                          return (
                            <ImageThumbnail
                              key={fileId}
                              file={file}
                              fileId={fileId}
                              workspaceId={workspaceId}
                              getAuthToken={getAuthToken}
                              onView={openFileViewer}
                              onDownload={(url) => window.open(url, '_blank')}
                            />
                          );
                        })}
                      </div>
                    )}
                    {/* CSV thumbnails */}
                    {csvFiles.map((file, index) => {
                      const fileId = `${message.id}-csv-${index}`;
                      return (
                        <CsvThumbnail
                          key={fileId}
                          file={file}
                          fileId={fileId}
                          workspaceId={workspaceId}
                          getAuthToken={getAuthToken}
                          onView={openFileViewer}
                        />
                      );
                    })}
                    {/* Other files - existing row layout */}
                    {otherFiles.map((file, index) => {
                      const fileId = `${message.id}-file-${index}`;
                      const isLoading = loadingFiles[fileId];
                      return (
                        <div key={fileId} className="inline-file-item" style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '8px 12px',
                          background: 'var(--bg-secondary)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '6px',
                          marginBottom: index < otherFiles.length - 1 ? '8px' : 0
                        }}
                        onClick={() => {
                          if (isMobile()) handleInlineFileView(file, fileId);
                        }}
                        >
                          {getFileIcon(file.filename, file.file_type)}
                          <span style={{ marginLeft: '0.5rem', flex: 1, fontSize: '14px' }}>
                            {file.filename} <span className="inline-file-size">({formatFileSize(file.file_size)})</span>
                          </span>
                          <div className="file-actions" style={{ marginLeft: '0.5rem', display: 'flex', gap: '8px' }}>
                            <button
                              onClick={() => handleInlineFileView(file, fileId)}
                              disabled={isLoading}
                              className="view-button"
                              style={{
                                background: 'transparent',
                                border: 'none',
                                padding: '4px 8px',
                                cursor: isLoading ? 'wait' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                color: 'var(--primary-color)',
                                fontSize: '12px',
                                opacity: isLoading ? 0.7 : 1
                              }}
                              title="View file"
                            >
                              {isLoading ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />}
                              View
                            </button>
                            <button
                              onClick={() => handleInlineFileDownload(file, fileId)}
                              disabled={isLoading}
                              className="download-button"
                              style={{
                                background: 'transparent',
                                border: 'none',
                                padding: '4px',
                                cursor: isLoading ? 'wait' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                color: 'var(--primary-color)',
                                opacity: isLoading ? 0.7 : 1
                              }}
                              title="Download file"
                            >
                              {isLoading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
              {message.can_save_report && onConvertToReport && (
                <div className="message-actions" style={{ marginTop: '0.75rem', display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => onConvertToReport(message)}
                    className="convert-button"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      padding: '4px 8px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      color: '#16a34a',
                      fontSize: '12px'
                    }}
                    title="Save this output as a report"
                  >
                    <BarChart3 size={12} />
                    Save as Report
                  </button>
                </div>
              )}
            </div>
            </div>
          </div>
        );

      case 'status':
        // Status messages are now handled by LoadingIndicator component
        return null;

      case 'file':
        return (
          <div key={message.id} className="message agent">
            <div className="agent-avatar-container">
              <img src="/maya.jpg" alt="Maya" className="agent-avatar" />
              <span className="agent-name">Maya</span>
              <span className="agent-timestamp">{formatTimestamp(message.timestamp)}</span>
            </div>
            <div className="message-content-wrapper">
              <div className="message-content">
                <div className="message-files">
                <div className="file-item" onClick={() => onDownloadFile(message.filename)}>
                  {getFileIcon(message.filename, message.fileType)}
                  <span style={{ marginLeft: '0.5rem' }}>
                    {message.filename} ({formatFileSize(message.fileSize)})
                  </span>
                  <Download size={14} style={{ marginLeft: '0.5rem' }} />
                </div>
              </div>
              </div>
            </div>
          </div>
        );

      case 'file_url':
        return (
          <div key={message.id} className="message agent">
            <div className="agent-avatar-container">
              <img src="/maya.jpg" alt="Maya" className="agent-avatar" />
              <span className="agent-name">Maya</span>
              <span className="agent-timestamp">{formatTimestamp(message.timestamp)}</span>
            </div>
            <div className="message-content-wrapper">
              <div className="message-content">
              <div className="message-files">
                <div className="file-item">
                  {getFileIcon(message.filename, message.fileType)}
                  <span style={{ marginLeft: '0.5rem' }}>
                    {message.filename} ({formatFileSize(message.fileSize)})
                  </span>
                  <div className="file-actions" style={{ marginLeft: '0.5rem', display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => openFileViewer(message.fileUrl, message.filename, message.fileType, message.fileSize)}
                      className="view-button"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        padding: '4px 8px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        color: '#0369a1',
                        fontSize: '12px'
                      }}
                      title="View file"
                    >
                      <Eye size={12} />
                      View
                    </button>
                    <a
                      href={message.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="file-link"
                      style={{ textDecoration: 'none', color: '#007bff' }}
                      title="Download file"
                    >
                      <Download size={14} />
                    </a>
                  </div>
                </div>
                {/* Show image preview for image files */}
                {message.fileType === 'image' && (
                  <div className="file-preview" style={{ marginTop: '0.5rem' }}>
                    <img
                      src={message.fileUrl}
                      alt={message.filename}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '300px',
                        borderRadius: '4px',
                        border: '1px solid #e0e0e0'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>
              {message.can_save_report && onConvertToReport && (
                <div className="message-actions" style={{ marginTop: '0.75rem', display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => onConvertToReport(message)}
                    className="convert-button"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      padding: '4px 8px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      color: '#16a34a',
                      fontSize: '12px'
                    }}
                    title="Save this output as a report"
                  >
                    <BarChart3 size={12} />
                    Save as Report
                  </button>
                </div>
              )}
              </div>
            </div>
          </div>
        );

      case 'error':
        return (
          <div key={message.id} className="message agent">
            <div className="message-header">
              <AlertCircle size={16} className="message-icon error-icon" />
              <span className="message-sender">System Error</span>
            </div>
            <div className="message-content error-message">
              <strong>Error:</strong> {message.content}
            </div>
            <div className="message-timestamp">
              {formatTimestamp(message.timestamp)}
            </div>
          </div>
        );

      case 'timeout_with_retry':
        return (
          <div key={message.id} className="message agent timeout-message">
            <div className="message-header">
              <AlertCircle size={16} className="message-icon warning-icon" />
              <span className="message-sender">System</span>
            </div>
            <div className="message-content">
              {message.content}
            </div>
            {message.retryAvailable && (
              <button
                onClick={onRetry}
                className="retry-button"
                style={{
                  marginTop: '0.75rem',
                  padding: '0.5rem 1rem',
                  background: '#0ea5e9',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = '#0284c7'}
                onMouseLeave={(e) => e.target.style.background = '#0ea5e9'}
              >
                <RefreshCw size={16} />
                Try Again
              </button>
            )}
            <div className="message-timestamp">
              {formatTimestamp(message.timestamp)}
            </div>
          </div>
        );

      case 'system':
        return (
          <div key={message.id} className="message system">
            <div className="message-header">
              <AlertCircle size={16} className="message-icon" />
              <span className="message-sender">System</span>
            </div>
            <div className="message-content">
              {message.content}
            </div>
            <div className="message-timestamp">
              {formatTimestamp(message.timestamp)}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (messages.length === 0) {
    return (
      <div className="messages-container">
        <div className="welcome-message">
          <div className="welcome-icon">
            <img src="/maya.jpg" alt="Maya" style={{ width: 120, height: 120, borderRadius: '50%', objectFit: 'cover' }} />
          </div>
          <h3>Hi, I'm Maya!</h3>
          <p>Your data assistant. Ask me anything — I can help you with:</p>
          <div className="welcome-features">
            <div className="feature-item">
              <div className="feature-icon"><Layers size={20} /></div>
              <span>Database queries and analysis</span>
            </div>
            <div className="feature-item">
              <div className="feature-icon"><BarChart2 size={20} /></div>
              <span>Data visualization and reporting</span>
            </div>
            <div className="feature-item">
              <div className="feature-icon"><Folder size={20} /></div>
              <span>File processing and analysis</span>
            </div>
            <div className="feature-item">
              <div className="feature-icon"><MessageSquare size={20} /></div>
              <span>General questions and assistance</span>
            </div>
          </div>
        </div>
        <div ref={messagesEndRef} />
      </div>
    );
  }

  return (
    <div className="messages-container">
      {messages.map(renderMessage)}
      <div ref={messagesEndRef} />
      <FileViewerModal
        isOpen={viewerModal.isOpen}
        onClose={closeFileViewer}
        fileUrl={viewerModal.fileUrl}
        filename={viewerModal.filename}
        fileType={viewerModal.fileType}
        fileSize={viewerModal.fileSize}
        onAddToChat={onAddToChat}
      />
      <MarkdownExpandModal
        isOpen={expandModal.isOpen}
        onClose={closeExpandModal}
        content={expandModal.content}
        timestamp={expandModal.timestamp}
      />
    </div>
  );
}

export default MessageList;
